/**
 * @module components/NetworksMap
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Station map display
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LayersControl } from 'react-leaflet';

import MyMapContainer from './MyMapContainer';
import NetworksMapLayer from './NetworksMapLayer';
import { Networks } from '../../libs/networks';
import StationStatusControl from './StationStatusControl';

import queryString from 'query-string';

import { useAppState } from '../../contexts/AppStateProvider';
import { useLanguageState } from '../../contexts/LanguageProvider';

// Constants
const DEFAULT_VIEWPORT = {
  center: [64.13, -90.34],
  zoom: 2,
}


const paramToBoolean = (param) => {
  if(param === '0') return false;
  return Boolean(param);
}

/**
 * Network map with layers per network specified in input object
 * @param {*} props 
 */
const NetworksMap = ({networks}) => {
  const [ state ] = useAppState();
  const [ lang ] = useLanguageState();

  // get the initial for url (optional)
  const {
    opened = true,
    closed = false,
    planned = false,
  } = queryString.parse(window.location.search);


  const [ showOpened, setShowOpened ] = useState(paramToBoolean(opened));
  const [ showClosed, setShowClosed ] = useState(paramToBoolean(closed));
  const [ showPlanned, setShowPlanned ] = useState(paramToBoolean(planned));

  let networkLayers = null;
  if (networks.length) {
    networkLayers = networks.map((network, i) => {
      const color = state.colorMarkerTable[i % state.colorMarkerTable.length];
      const name = (network.code in lang.networkCodes)?lang.networkCodes[network.code]:network.code;
      return (<LayersControl.Overlay
        key={network.code}
        checked
        name={`<span style="border:1px solid ${color}; border-left-width:15px; padding:5px">${name}</span>`}
      >
        <NetworksMapLayer
          key={network.code}
          networkCode={network.code}
          stations={network.stations}
          color={color}
          showOpened={showOpened}
          showClosed={showClosed}
          showPlanned={showPlanned}
        />
      </LayersControl.Overlay>);
    });
  }

  return (
    <MyMapContainer
      center={DEFAULT_VIEWPORT.center}
      zoom={DEFAULT_VIEWPORT.zoom}
    >
      <LayersControl position="topright">
        {networkLayers}
      </LayersControl>
      <StationStatusControl
        position="bottomright"
        showOpened={showOpened}
        setShowOpened={setShowOpened}
        showClosed={showClosed}
        setShowClosed={setShowClosed}
        showPlanned={showPlanned}
        setShowPlanned={(planned)?setShowPlanned:undefined}
      />
    </MyMapContainer>
  );
};


NetworksMap.propTypes = {
  /**
   * List of networks with stations
   */
  networks: PropTypes.instanceOf(Networks),
};


NetworksMap.defaultProps = {
  networks: new Networks(),
};


export default NetworksMap;